import React from 'react'
import { Document, Page, StyleSheet, View, Text } from '@react-pdf/renderer'
import { Transcript } from '../../../swagger'
import { Font } from '@react-pdf/renderer'
import fullName from '../../../utils/fullName'
import studentKeyToStudentIdForTranscript from '../../../utils/studentKeyToStudentIdForTranscript'
import { dateToDashString, IS_UTC } from '../../../utils/dateUtility'

interface TranscriptPDFProps {
  transcripts: Transcript[]
  actingAs: 'parent' | number
  scale?: number
}

const TranscriptPDF: React.FC<TranscriptPDFProps> = ({
  transcripts,
  actingAs,
  scale = 1,
}) => {
  Font.registerHyphenationCallback((word) => [word])

  // Helper function to scale numeric values
  const scaleValue = (value: number): number => value * scale

  const styles = StyleSheet.create({
    page: {
      fontFamily: 'Open Sans',
      fontSize: scaleValue(8),
      padding: scaleValue(24),
    },
    title: {
      fontSize: scaleValue(12),
      fontWeight: 600,
      textAlign: 'center',
      marginBottom: scaleValue(8),
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: scaleValue(6),
    },
    columns: {
      display: 'flex',
      flexDirection: 'row',
      gap: scaleValue(6),
    },
    info: {
      paddingLeft: scaleValue(4),
      width: '100%',
    },
    infoNoPadding: {
      width: '100%',
    },
    grid: {
      display: 'flex',
      flexDirection: 'column',
      gap: scaleValue(2),
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
    },
    label: {
      width: '50%',
      margin: 0,
    },
    emailLabel: {
      width: '19.5%',
      margin: 0,
    },
    value: {
      width: '100%',
      margin: 0,
    },
    space: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingRight: 4,
    },
    spaceNoPadding: {
      display: 'flex',
      flexDirection: 'row',
    },
    flexCol: {
      display: 'flex',
      flexDirection: 'column',
    },
    flexRow: {
      display: 'flex',
      flexDirection: 'row',
    },
    yearGrid: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingRight: 2,
    },
    classHeader: {
      textDecoration: 'underline',
      fontWeight: 'bold',
    },
    border: {
      border: '1px solid black',
    },
    borderTop: {
      borderTop: '1px solid black',
    },
    borderRight: {
      borderRight: '1px solid black',
    },
    borderX: {
      borderLeft: '1px solid black',
      borderRight: '1px solid black',
    },
    borderY: {
      borderTop: '1px solid black',
      borderBottom: '1px solid black',
    },
    alignItemsEnd: {
      alignItems: 'flex-end',
    },
    alignItemsCenter: {
      alignItems: 'center',
    },
    bold: {
      fontWeight: 'bold',
    },
    paddingTop2: {
      paddingTop: scaleValue(2),
    },
    paddingLeft2: {
      paddingLeft: scaleValue(2),
    },
    paddingBottom2: {
      paddingBottom: scaleValue(2),
    },
    marginBottom24: {
      marginBottom: scaleValue(16),
    },
    notes: {
      minHeight: scaleValue(60),
      textAlign: 'justify',
      marginRight: scaleValue(6),
    },
    small: {
      marginLeft: scaleValue(8),
      maxWidth: '35%',
    },
    maxWidth50: {
      maxWidth: '50%',
    },
    signatureSection: {
      marginTop: scaleValue(16),
    },
    signatureLine: {
      marginTop: scaleValue(24),
      marginBottom: scaleValue(12),
    },
    signatureRow: {
      marginTop: scaleValue(16),
      marginBottom: scaleValue(12),
    },
    lineM: {
      borderBottom: '1px solid black',
      minWidth: scaleValue(220),
    },
    lineL: {
      borderBottom: '1px solid black',
      minWidth: scaleValue(473),
    },
    table: {
      margin: `${scaleValue(2)}px 0`,
    },
    tableCell: {
      padding: scaleValue(2),
    },
    gradingScaleTable: {
      width: '100%',
      margin: `${scaleValue(2)}px 0`,
    },
    gradingScaleHeaderCell: {
      padding: scaleValue(2),
      width: '33.33%',
      textAlign: 'center',
    },
    gradingScaleCell: {
      padding: scaleValue(2),
      width: '33.33%',
      textAlign: 'center',
    },
    weightTable: {
      width: '100%',
      margin: `${scaleValue(2)}px 0`,
      marginLeft: scaleValue(8),
    },
    weightHeaderCell: {
      padding: scaleValue(2),
      textAlign: 'center',
    },
    weightNameHeaderCell: {
      padding: scaleValue(2),
      width: '70%',
      textAlign: 'center',
    },
    weightValueHeaderCell: {
      padding: scaleValue(2),
      width: '30%',
      textAlign: 'center',
    },
    weightNameCell: {
      padding: scaleValue(2),
      width: '70%',
      textAlign: 'center',
    },
    weightCell: {
      padding: scaleValue(2),
      width: '30%',
      textAlign: 'center',
    },
  })

  const renderNotes = (notes?: string) => {
    if (!notes) return null

    return (
      <View style={styles.notes}>
        <Text style={{ margin: `0 0 ${scaleValue(6)}px 0` }}>Notes</Text>
        {notes.split('\n\n').map((note, index) => (
          <Text key={index} style={{ margin: `0 0 ${scaleValue(6)}px 0` }}>
            {note}
          </Text>
        ))}
      </View>
    )
  }

  const renderCourseWork = (
    courseWork: { courseName: string; letterGrade: string; credits: number }[],
    type: 'courseName' | 'letterGrade' | 'credits'
  ) => {
    return courseWork.map((course, index) => {
      if (type === 'credits') {
        return <Text key={index}>{course.credits.toFixed(1)}</Text>
      }
      return (
        <Text key={index}>
          {type === 'letterGrade' ? course[type] || ' ' : course[type]}
        </Text>
      )
    })
  }

  const renderGradingScaleTable = (gradingScale: {
    gradingScaleDetails?: {
      scaleRange: string
      letterGrade: string
      credit?: number
    }[]
  }) => {
    if (!gradingScale?.gradingScaleDetails) return null

    return (
      <View style={styles.gradingScaleTable}>
        <View style={[styles.row, styles.borderTop, styles.borderX]}>
          <Text style={[styles.gradingScaleHeaderCell, styles.borderRight]}>
            Grade Scale
          </Text>
          <Text style={[styles.gradingScaleHeaderCell, styles.borderRight]}>
            Letter Grade
          </Text>
          <Text style={[styles.gradingScaleHeaderCell, styles.borderRight]}>
            Grade Points
          </Text>
        </View>
        {gradingScale.gradingScaleDetails.map(
          (
            detail: {
              scaleRange: string
              letterGrade: string
              credit?: number
            },
            index: number
          ) => {
            const isLast =
              index === (gradingScale.gradingScaleDetails?.length ?? 0) - 1
            return (
              <View
                key={index}
                style={[
                  styles.row,
                  isLast ? styles.borderY : styles.borderTop,
                  styles.borderX,
                ]}
              >
                <Text style={[styles.gradingScaleCell, styles.borderRight]}>
                  {detail.scaleRange}
                </Text>
                <Text style={[styles.gradingScaleCell, styles.borderRight]}>
                  {detail.letterGrade}
                </Text>
                <Text style={[styles.gradingScaleCell, styles.borderRight]}>
                  {detail.credit?.toFixed(2)}
                </Text>
              </View>
            )
          }
        )}
      </View>
    )
  }

  const renderWeightTable = (gradingScale: {
    gradingScaleWeight?: { weightName: string; weight: number }[]
  }) => {
    if (!gradingScale?.gradingScaleWeight?.length) return null

    return (
      <View style={styles.weightTable}>
        <View style={[styles.row, styles.borderTop, styles.borderX]}>
          <Text style={[styles.weightNameHeaderCell, styles.borderRight]}>
            Weight Name
          </Text>
          <Text style={[styles.weightValueHeaderCell, styles.borderRight]}>
            Weight
          </Text>
        </View>
        {gradingScale.gradingScaleWeight.map(
          (weight: { weightName: string; weight: number }, index: number) => {
            const isLast =
              index === (gradingScale.gradingScaleWeight?.length ?? 0) - 1
            return (
              <View
                key={index}
                style={[
                  styles.row,
                  isLast ? styles.borderY : styles.borderTop,
                  styles.borderX,
                ]}
              >
                <Text style={[styles.weightNameCell, styles.borderRight]}>
                  {weight.weightName}
                </Text>
                <Text style={[styles.weightCell, styles.borderRight]}>
                  {weight.weight.toFixed(1)}
                </Text>
              </View>
            )
          }
        )}
      </View>
    )
  }

  const renderTranscriptContent = (transcript: Transcript) => {
    const {
      student,
      studentAddress,
      schoolAddress,
      studentAddressSameAsSchool,
      studentDateOfBirth,
      schoolName,
      dateGraduation,
      gpa,
      summary,
      studentEmail,
      totalCredits,
      transcriptYear1,
      transcriptYear2,
      transcriptYear3,
      transcriptYear4,
      gradingScale,
    } = transcript

    const address = !studentAddressSameAsSchool ? studentAddress : schoolAddress

    return (
      <View style={styles.container}>
        {/* Title */}
        <Text style={styles.title}>Official High School Transcript</Text>

        {/* Student & School Information */}
        <View wrap={false} style={styles.container}>
          <View style={styles.columns}>
            {/* Student Information */}
            <View style={styles.info}>
              <Text style={styles.paddingBottom2}>Student Information</Text>
              <View style={styles.grid}>
                <View style={styles.row}>
                  <Text style={styles.label}>Name:</Text>
                  <Text style={styles.value}>
                    {fullName({
                      firstName: student.firstName,
                      lastName: student.lastName,
                    })}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Student Id:</Text>
                  <Text style={styles.value}>
                    {studentKeyToStudentIdForTranscript(student.studentKey)}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Address:</Text>
                  <Text style={styles.value}>{address?.streetAddress1}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>City, State Zip:</Text>
                  <Text style={styles.value}>
                    {address?.city}, {address?.state} {address?.zip}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Date of Birth:</Text>
                  <Text style={styles.value}>
                    {studentDateOfBirth
                      ? dateToDashString(studentDateOfBirth, IS_UTC)
                      : ''}
                  </Text>
                </View>
              </View>
            </View>

            {/* School Information */}
            <View style={styles.info}>
              <Text style={styles.paddingBottom2}>School Information</Text>
              <View style={styles.grid}>
                <View style={styles.row}>
                  <Text style={styles.label}>School Name:</Text>
                  <Text style={styles.value}>{schoolName}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Address:</Text>
                  <Text style={styles.value}>
                    {schoolAddress.streetAddress1}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>City, State Zip:</Text>
                  <Text style={styles.value}>
                    {schoolAddress.city}, {schoolAddress.state}{' '}
                    {schoolAddress.zip}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Graduation Date:</Text>
                  <Text style={styles.value}>
                    {dateGraduation
                      ? dateToDashString(dateGraduation, IS_UTC)
                      : ''}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Student Email */}
          <View style={[styles.info, styles.paddingTop2]}>
            <View style={styles.row}>
              <Text style={styles.emailLabel}>Student Email:</Text>
              <Text style={styles.value}>{studentEmail}</Text>
            </View>
          </View>
        </View>

        {/* 9th & 10th Grade */}
        <View wrap={false} style={styles.columns}>
          {/* 9th Grade */}
          <View style={[styles.info, styles.border]}>
            <View style={styles.space}>
              <Text>Grade: 9th</Text>
              <Text>
                {transcriptYear1?.academicYear
                  ? `${transcriptYear1.academicYear}-${
                      transcriptYear1.academicYear + 1
                    }`
                  : ''}
              </Text>
            </View>
            <View style={styles.flexCol}>
              <View style={styles.yearGrid}>
                <View>
                  <Text style={styles.classHeader}>Class</Text>
                  {renderCourseWork(
                    transcriptYear1?.transcriptYearCourseWork ?? [],
                    'courseName'
                  )}
                </View>
                <View>
                  <Text style={styles.classHeader}>Grade</Text>
                  {renderCourseWork(
                    transcriptYear1?.transcriptYearCourseWork ?? [],
                    'letterGrade'
                  )}
                </View>
                <View style={styles.alignItemsEnd}>
                  <Text style={styles.classHeader}>Credits</Text>
                  {renderCourseWork(
                    transcriptYear1?.transcriptYearCourseWork ?? [],
                    'credits'
                  )}
                </View>
              </View>
              <View
                style={[
                  styles.flexRow,
                  styles.alignItemsEnd,
                  styles.space,
                  styles.bold,
                  styles.marginBottom24,
                ]}
              >
                <Text>GPA: {(transcriptYear1?.gpa ?? 0).toFixed(2)}</Text>
                <Text>
                  Total Credits:{' '}
                  {(transcriptYear1?.totalCredits ?? 0).toFixed(2)}
                </Text>
              </View>
            </View>
            {renderNotes(transcriptYear1?.notes)}
          </View>

          {/* 10th Grade */}
          <View style={[styles.info, styles.border]}>
            <View style={styles.space}>
              <Text>Grade: 10th</Text>
              <Text>
                {transcriptYear2?.academicYear
                  ? `${transcriptYear2.academicYear}-${
                      transcriptYear2.academicYear + 1
                    }`
                  : ''}
              </Text>
            </View>
            <View style={styles.flexCol}>
              <View style={styles.yearGrid}>
                <View>
                  <Text style={styles.classHeader}>Class</Text>
                  {renderCourseWork(
                    transcriptYear2?.transcriptYearCourseWork ?? [],
                    'courseName'
                  )}
                </View>
                <View>
                  <Text style={styles.classHeader}>Grade</Text>
                  {renderCourseWork(
                    transcriptYear2?.transcriptYearCourseWork ?? [],
                    'letterGrade'
                  )}
                </View>
                <View style={styles.alignItemsEnd}>
                  <Text style={styles.classHeader}>Credits</Text>
                  {renderCourseWork(
                    transcriptYear2?.transcriptYearCourseWork ?? [],
                    'credits'
                  )}
                </View>
              </View>
              <View
                style={[
                  styles.flexRow,
                  styles.alignItemsEnd,
                  styles.space,
                  styles.bold,
                  styles.marginBottom24,
                ]}
              >
                <Text>GPA: {(transcriptYear2?.gpa ?? 0).toFixed(2)}</Text>
                <Text>
                  Total Credits:{' '}
                  {(transcriptYear2?.totalCredits ?? 0).toFixed(2)}
                </Text>
              </View>
            </View>
            {renderNotes(transcriptYear2?.notes)}
          </View>
        </View>

        {/* 11th & 12th Grade */}
        <View wrap={false} style={styles.columns}>
          {/* 11th Grade */}
          <View style={[styles.info, styles.border]}>
            <View style={styles.space}>
              <Text>Grade: 11th</Text>
              <Text>
                {transcriptYear3?.academicYear
                  ? `${transcriptYear3.academicYear}-${
                      transcriptYear3.academicYear + 1
                    }`
                  : ''}
              </Text>
            </View>
            <View style={styles.flexCol}>
              <View style={styles.yearGrid}>
                <View>
                  <Text style={styles.classHeader}>Class</Text>
                  {renderCourseWork(
                    transcriptYear3?.transcriptYearCourseWork ?? [],
                    'courseName'
                  )}
                </View>
                <View>
                  <Text style={styles.classHeader}>Grade</Text>
                  {renderCourseWork(
                    transcriptYear3?.transcriptYearCourseWork ?? [],
                    'letterGrade'
                  )}
                </View>
                <View style={styles.alignItemsEnd}>
                  <Text style={styles.classHeader}>Credits</Text>
                  {renderCourseWork(
                    transcriptYear3?.transcriptYearCourseWork ?? [],
                    'credits'
                  )}
                </View>
              </View>
              <View
                style={[
                  styles.flexRow,
                  styles.alignItemsEnd,
                  styles.space,
                  styles.bold,
                  styles.marginBottom24,
                ]}
              >
                <Text>GPA: {(transcriptYear3?.gpa ?? 0).toFixed(2)}</Text>
                <Text>
                  Total Credits:{' '}
                  {(transcriptYear3?.totalCredits ?? 0).toFixed(2)}
                </Text>
              </View>
            </View>
            {renderNotes(transcriptYear3?.notes)}
          </View>

          {/* 12th Grade */}
          <View style={[styles.info, styles.border]}>
            <View style={styles.space}>
              <Text>Grade: 12th</Text>
              <Text>
                {transcriptYear4?.academicYear
                  ? `${transcriptYear4.academicYear}-${
                      transcriptYear4.academicYear + 1
                    }`
                  : ''}
              </Text>
            </View>
            <View style={styles.flexCol}>
              <View style={styles.yearGrid}>
                <View>
                  <Text style={styles.classHeader}>Class</Text>
                  {renderCourseWork(
                    transcriptYear4?.transcriptYearCourseWork ?? [],
                    'courseName'
                  )}
                </View>
                <View>
                  <Text style={styles.classHeader}>Grade</Text>
                  {renderCourseWork(
                    transcriptYear4?.transcriptYearCourseWork ?? [],
                    'letterGrade'
                  )}
                </View>
                <View style={styles.alignItemsEnd}>
                  <Text style={styles.classHeader}>Credits</Text>
                  {renderCourseWork(
                    transcriptYear4?.transcriptYearCourseWork ?? [],
                    'credits'
                  )}
                </View>
              </View>
              <View
                style={[
                  styles.flexRow,
                  styles.alignItemsEnd,
                  styles.space,
                  styles.bold,
                  styles.marginBottom24,
                ]}
              >
                <Text>GPA: {(transcriptYear4?.gpa ?? 0).toFixed(2)}</Text>
                <Text>
                  Total Credits:{' '}
                  {(transcriptYear4?.totalCredits ?? 0).toFixed(2)}
                </Text>
              </View>
            </View>
            {renderNotes(transcriptYear4?.notes)}
          </View>
        </View>

        {/* Tables & Summary */}
        <View wrap={false} style={styles.columns}>
          <View style={[styles.infoNoPadding, styles.spaceNoPadding]}>
            <View style={styles.infoNoPadding}>
              {gradingScale && renderGradingScaleTable(gradingScale)}
            </View>
            {gradingScale && renderWeightTable(gradingScale)}
          </View>

          <View style={styles.info}>
            <View
              style={[
                styles.flexRow,
                styles.maxWidth50,
                styles.paddingBottom2,
                styles.space,
              ]}
            >
              <View style={[styles.flexCol, styles.alignItemsCenter]}>
                <Text>Total Credits</Text>
                <Text style={styles.bold}>{totalCredits}</Text>
              </View>
              <View style={[styles.flexCol, styles.alignItemsCenter]}>
                <Text>Cumulative GPA</Text>
                <Text style={styles.bold}>{gpa}</Text>
              </View>
            </View>
            <View style={[styles.border, styles.paddingLeft2]}>
              <View style={styles.paddingLeft2}>
                <Text>NOTES:</Text>
              </View>
              <View style={styles.notes}>
                <Text>{summary}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Signature Section */}
        {actingAs === 'parent' && (
          <View wrap={false} style={styles.signatureSection}>
            <Text>
              I self-certify and affirm that this is the official transcript and
              record of{' '}
              {fullName({
                firstName: student.firstName,
                lastName: student.lastName,
              })}
              's academic studies through {dateGraduation?.getUTCDate()}{' '}
              {dateGraduation?.toLocaleString('default', { month: 'long' })},{' '}
              {dateGraduation?.getUTCFullYear() ?? ''}
            </Text>
            <View
              style={[
                styles.flexRow,
                styles.alignItemsEnd,
                styles.signatureLine,
              ]}
            >
              <Text>Name:</Text>
              <View style={styles.lineL}>&nbsp;</View>
            </View>
            <View
              style={[
                styles.flexRow,
                styles.alignItemsEnd,
                styles.signatureRow,
              ]}
            >
              <Text>Signature:</Text>
              <View style={styles.lineM}>&nbsp;</View>
              <Text>Date:</Text>
              <View style={styles.lineM}>&nbsp;</View>
            </View>
            <View style={styles.paddingTop2}>
              <Text>
                [Enter administrator's full name](Administrator of Record)
              </Text>
            </View>
          </View>
        )}
      </View>
    )
  }

  return (
    <Document>
      {transcripts.map((transcriptDetail) => (
        <Page key={transcriptDetail.transcriptKey} style={styles.page}>
          {renderTranscriptContent(transcriptDetail)}
        </Page>
      ))}
    </Document>
  )
}

export default TranscriptPDF
